import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Divider } from '../../../components/primitives/Divider';
import { Select } from '../../../components/primitives/Select';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useCurrentOrgRole } from '../../../hooks/useAuthOrgRole';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useOrgSettings, useUpdateOrgSettings } from '../../../hooks/useOrgSettings';
import { SettingsSection } from './SettingsSection';

export function BoardVisibilitySection() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;
  const { t } = useTranslation();
  const role = useCurrentOrgRole();
  const canManage = role === 'owner' || role === 'admin';

  const { data: settings, isLoading } = useOrgSettings(orgId);
  const updateSettings = useUpdateOrgSettings();

  const [visibility, setVisibility] = useState('shared');

  useEffect(() => {
    if (settings) {
      setVisibility(settings.boardVisibility ?? 'shared');
    }
  }, [settings]);

  const options = [
    { value: 'individual', label: t('settings.boardVisibility.individual'), description: t('settings.boardVisibility.individualDesc') },
    { value: 'shared', label: t('settings.boardVisibility.shared'), description: t('settings.boardVisibility.sharedDesc') },
    { value: 'hybrid', label: t('settings.boardVisibility.hybrid'), description: t('settings.boardVisibility.hybridDesc') },
  ];

  const handleSave = () => {
    if (!orgId) return;
    updateSettings.mutate({ orgId: orgId!, boardVisibility: visibility });
  };

  if (isLoading || role === undefined) {
    return (
      <Box
        backgroundColor={colors.background.card}
        borderRadius="xl"
        borderWidth={1}
        borderColor={colors.border.subtle}
        padding="xl"
        alignItems="center"
        justifyContent="center"
        minHeight={200}
      >
        <ActivityIndicator size="large" color={colors.primary[500]} />
        <Text variant="bodySmall" color={colors.text.secondary} style={styles.loadingText}>
          {t('settings.boardVisibility.loading')}
        </Text>
      </Box>
    );
  }

  return (
    <SettingsSection title={t('settings.boardVisibility.title')}>
      <Text variant="body" color={colors.text.secondary}>
        {t('settings.boardVisibility.description')}
      </Text>

      <Divider color={colors.border.muted} />

      {canManage ? (
        <VStack spacing="sm" style={{ zIndex: tokens.zIndex.dropdown }}>
          <Select
            variant="dropdown"
            options={options}
            value={visibility}
            onChange={(v) => setVisibility(v)}
            testID="board-visibility-select"
          />
          <Text variant="caption" color={colors.text.tertiary}>
            {options.find((o) => o.value === visibility)?.description}
          </Text>
        </VStack>
      ) : (
        <VStack spacing="sm">
          <Text variant="body" color={colors.text.secondary}>
            {options.find((o) => o.value === visibility)?.label ?? visibility}
          </Text>
          <Text variant="caption" color={colors.text.tertiary}>
            {options.find((o) => o.value === visibility)?.description}
          </Text>
        </VStack>
      )}

      {updateSettings.isSuccess && (
        <Text variant="caption" color={colors.status.success.main}>
          {t('settings.boardVisibility.saveSuccess')}
        </Text>
      )}

      {updateSettings.isError && (
        <Text variant="caption" color={colors.status.error.main}>
          {t('settings.boardVisibility.saveError')}
        </Text>
      )}

      {canManage && (
        <HStack spacing="sm" justify="end">
          <Button
            variant="primary"
            size="md"
            onPress={handleSave}
            loading={updateSettings.isPending}
            disabled={updateSettings.isPending}
            testID="board-visibility-save"
          >
            {t('common.actions.saveChanges')}
          </Button>
        </HStack>
      )}
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  loadingText: {
    marginTop: tokens.spacing.md,
  },
});
